import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchMeetingMinutes } from '../services/meetingMinutesService';

const CACHE_TTL_MS = 60 * 1000;
const minutesCache = new Map();

const buildCacheKey = (bodyId, filters) => {
  return `${bodyId}:${filters.page}:${filters.limit}:${filters.search}:${filters.date}`;
};

export const useMeetingMinutes = (bodyId, filters = {}) => {
  const { page = 1, limit = 10, search = '', date = '' } = filters;
  
  const [data, setData] = useState({ 
    meetingMinutes: [], 
    total: 0, 
    pagination: { page: 1, limit: 10, totalPages: 1, hasMore: false } 
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const loadMinutes = useCallback(async ({ useCache = true } = {}) => {
    if (!bodyId) return;

    const cacheKey = buildCacheKey(bodyId, { page, limit, search, date });
    const cached = minutesCache.get(cacheKey);
    if (useCache && cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      setData(cached.data);
      setLoading(false);
      setError(null);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const response = await fetchMeetingMinutes({
        bodyId,
        page,
        limit,
        search,
        date,
        signal: controller.signal
      });

      const nextData = {
        meetingMinutes: response.meetingMinutes || [],
        total: response.total || 0,
        pagination: response.pagination || { page, limit, totalPages: 1, hasMore: false }
      };

      minutesCache.set(cacheKey, { data: nextData, timestamp: Date.now() });
      setData(nextData);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message || 'Unable to load meeting minutes');
    } finally {
      setLoading(false);
    }
  }, [bodyId, page, limit, search, date]);

  useEffect(() => {
    loadMinutes({ useCache: true });

    return () => {
      abortRef.current?.abort();
    };
  }, [loadMinutes]);

  const refetch = useCallback(() => loadMinutes({ useCache: false }), [loadMinutes]);

  return {
    meetingMinutes: data.meetingMinutes,
    total: data.total,
    pagination: data.pagination,
    loading,
    error,
    refetch
  };
};

export default useMeetingMinutes;
